import { useEffect, useState } from 'react';
import { api, ApiCallError, type ChatHistoryMsg } from '../api/client';
import { formatRelativeTime } from '@xiaopeiban/shared';
import type { ChildSummary } from '@xiaopeiban/shared';

export default function ChatArchivePage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [childId, setChildId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatHistoryMsg[]>([]);
  const [activePetId, setActivePetId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.listChildren()
      .then(c => {
        setChildren(c.children);
        if (c.children.length > 0) setChildId(c.children[0].id);
      })
      .catch(e => setErr(niceError(e)));
  }, []);

  useEffect(() => {
    if (childId == null) return;
    setLoading(true);
    setActivePetId(null);
    api.chatHistory(childId, 200)
      .then(r => {
        setMessages(r.messages);
        setActivePetId(r.messages[0]?.pet_id ?? null);
      })
      .catch(e => setErr(niceError(e)))
      .finally(() => setLoading(false));
  }, [childId]);

  const petTabs = messages.reduce<Array<{
    pet_id: number;
    pet_nickname: string;
    species: string;
    breed_name: string;
    count: number;
  }>>((tabs, m) => {
    const tab = tabs.find(t => t.pet_id === m.pet_id);
    if (tab) {
      tab.count += 1;
      return tabs;
    }
    tabs.push({
      pet_id: m.pet_id,
      pet_nickname: m.pet_nickname,
      species: m.species,
      breed_name: m.breed_name,
      count: 1,
    });
    return tabs;
  }, []);
  const selectedPetId = activePetId ?? petTabs[0]?.pet_id ?? null;
  const visibleMessages = selectedPetId == null ? messages : messages.filter(m => m.pet_id === selectedPetId);

  // Group selected pet messages by thread.
  const grouped: Array<{ thread_id: number; pet: string; items: ChatHistoryMsg[] }> = [];
  for (const m of visibleMessages) {
    let last = grouped[grouped.length - 1];
    if (!last || last.thread_id !== m.thread_id) {
      last = { thread_id: m.thread_id, pet: m.pet_nickname, items: [] };
      grouped.push(last);
    }
    last.items.push(m);
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">💬 聊天记录</div>
        <div className="page-subtitle">查看孩子和 AI 宠物的所有对话</div>
      </div>

      {err && <div className="error-box">{err}</div>}

      {children.length > 1 && (
        <div className="card">
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {children.map(c => (
              <button
                key={c.id}
                className={`btn ${c.id === childId ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setChildId(c.id)}
              >
                {c.avatar || '👶'} {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner spinner-lg" /></div>}

      {!loading && messages.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💭</div>
          <div className="muted">还没有聊天记录</div>
        </div>
      )}

      {!loading && petTabs.length > 0 && (
        <div className="card chat-pet-tabs-card">
          <div className="chat-pet-tabs" role="tablist" aria-label="按动物查看聊天记录">
            {petTabs.map(p => (
              <button
                key={p.pet_id}
                type="button"
                role="tab"
                aria-selected={p.pet_id === selectedPetId}
                className={`chat-pet-tab ${p.pet_id === selectedPetId ? 'active' : ''}`}
                onClick={() => setActivePetId(p.pet_id)}
              >
                <span className="chat-pet-tab-icon" aria-hidden>{speciesEmoji(p.species)}</span>
                <span className="chat-pet-tab-copy">
                  <b>{p.pet_nickname}</b>
                  <small>{p.breed_name} · {p.count} 条</small>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {grouped.map(g => (
        <div key={g.thread_id} className="card">
          <div style={{ fontWeight: 800, marginBottom: 10, color: 'var(--primary-strong)' }}>
            🐹 {g.pet} · 对话 #{g.thread_id}
          </div>
          {g.items.map(m => (
            <div
              key={m.id}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 8,
              }}
            >
              <div style={{
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: m.role === 'user'
                  ? '14px 14px 4px 14px'
                  : '14px 14px 14px 4px',
                background: m.role === 'user' ? '#fde68a' : '#e0e7ff',
                color: m.role === 'user' ? '#92400e' : '#3730a3',
                fontSize: 14,
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2, opacity: 0.7 }}>
                  {m.role === 'user' ? '小朋友' : '宠物'} · {formatRelativeTime(m.created_at)}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}

function speciesEmoji(species: string): string {
  const map: Record<string, string> = {
    hamster: '🐹',
    rabbit: '🐰',
    dog: '🐕',
    dragon: '🐉',
    cat: '🐱',
    fox: '🦊',
    bear: '🐻',
    deer: '🦌',
  };
  return map[species] || '🐾';
}
